// src/features/document/complianceLogic.ts
// Pure logic + label maps for the document compliance surfaces
// (lifecycle, versions, acknowledgments, approval pipelines, registers, clauses,
// numbering, policies). Zero React imports.

import type { Entity } from '@core/types/entity';
import type { BadgeVariant } from './logic';

// ---------------------------------------------------------------------------
// Target addressing — lifecycle/version/ack/pipeline RPCs take (target_type, target_id)
// ---------------------------------------------------------------------------

/** Build the target Entity for a standalone document's lifecycle RPCs. */
export function documentTarget(documentId: string, productId: string): Entity {
  return {
    type_id: '',
    type_name: 'document',
    id: documentId,
    platform_id: '',
    tenant_id: '',
    product_id: productId,
  };
}

// ---------------------------------------------------------------------------
// Enum → { variant, labelKey } maps
// ---------------------------------------------------------------------------

interface Mapping {
  variant: BadgeVariant;
  labelKey: string;
}

export const clauseStatusMap: Record<string, Mapping> = {
  CLAUSE_STATUS_DRAFT: { variant: 'secondary', labelKey: 'document.clause.status.draft' },
  CLAUSE_STATUS_REVIEW: { variant: 'warning', labelKey: 'document.clause.status.review' },
  CLAUSE_STATUS_APPROVED: { variant: 'success', labelKey: 'document.clause.status.approved' },
  CLAUSE_STATUS_DEPRECATED: { variant: 'error', labelKey: 'document.clause.status.deprecated' },
};

export const clauseCategoryKeys: string[] = [
  'CLAUSE_CATEGORY_LIABILITY',
  'CLAUSE_CATEGORY_CONFIDENTIALITY',
  'CLAUSE_CATEGORY_IP',
  'CLAUSE_CATEGORY_TERMINATION',
  'CLAUSE_CATEGORY_GOVERNING_LAW',
  'CLAUSE_CATEGORY_FORCE_MAJEURE',
  'CLAUSE_CATEGORY_PAYMENT_TERMS',
  'CLAUSE_CATEGORY_WARRANTY',
  'CLAUSE_CATEGORY_INDEMNIFICATION',
  'CLAUSE_CATEGORY_ASSIGNMENT',
  'CLAUSE_CATEGORY_NOTICES',
  'CLAUSE_CATEGORY_ENTIRE_AGREEMENT',
  'CLAUSE_CATEGORY_AMENDMENTS',
  'CLAUSE_CATEGORY_SEVERABILITY',
  'CLAUSE_CATEGORY_OTHER',
];

export function clauseCategoryLabelKey(cat: string | undefined): string {
  if (!cat || cat === 'CLAUSE_CATEGORY_UNSPECIFIED') return 'document.clause.category.unspecified';
  const suffix = cat.replace('CLAUSE_CATEGORY_', '').toLowerCase();
  return `document.clause.category.${camel(suffix)}`;
}

export const acknowledgmentStatusMap: Record<string, Mapping> = {
  ACKNOWLEDGMENT_STATUS_PENDING: { variant: 'warning', labelKey: 'document.ack.status.pending' },
  ACKNOWLEDGMENT_STATUS_ACKNOWLEDGED: { variant: 'success', labelKey: 'document.ack.status.acknowledged' },
  ACKNOWLEDGMENT_STATUS_OVERDUE: { variant: 'error', labelKey: 'document.ack.status.overdue' },
  ACKNOWLEDGMENT_STATUS_WAIVED: { variant: 'secondary', labelKey: 'document.ack.status.waived' },
};

export const pipelineExecutionStatusMap: Record<string, Mapping> = {
  PIPELINE_EXECUTION_STATUS_PENDING: { variant: 'secondary', labelKey: 'document.pipeline.execStatus.pending' },
  PIPELINE_EXECUTION_STATUS_IN_PROGRESS: { variant: 'primary', labelKey: 'document.pipeline.execStatus.inProgress' },
  PIPELINE_EXECUTION_STATUS_COMPLETED: { variant: 'success', labelKey: 'document.pipeline.execStatus.completed' },
  PIPELINE_EXECUTION_STATUS_FAILED: { variant: 'error', labelKey: 'document.pipeline.execStatus.failed' },
  PIPELINE_EXECUTION_STATUS_CANCELLED: { variant: 'secondary', labelKey: 'document.pipeline.execStatus.cancelled' },
};

export const stageExecutionStatusMap: Record<string, Mapping> = {
  STAGE_EXECUTION_STATUS_PENDING: { variant: 'secondary', labelKey: 'document.pipeline.stageStatus.pending' },
  STAGE_EXECUTION_STATUS_IN_PROGRESS: { variant: 'primary', labelKey: 'document.pipeline.stageStatus.inProgress' },
  STAGE_EXECUTION_STATUS_COMPLETED: { variant: 'success', labelKey: 'document.pipeline.stageStatus.completed' },
  STAGE_EXECUTION_STATUS_FAILED: { variant: 'error', labelKey: 'document.pipeline.stageStatus.failed' },
  STAGE_EXECUTION_STATUS_SKIPPED: { variant: 'secondary', labelKey: 'document.pipeline.stageStatus.skipped' },
};

export const pipelineActionMap: Record<string, Mapping> = {
  DOCUMENT_PIPELINE_ACTION_ACKNOWLEDGE: { variant: 'default', labelKey: 'document.pipeline.action.acknowledge' },
  DOCUMENT_PIPELINE_ACTION_APPROVE: { variant: 'primary', labelKey: 'document.pipeline.action.approve' },
  DOCUMENT_PIPELINE_ACTION_REVIEW: { variant: 'warning', labelKey: 'document.pipeline.action.review' },
  DOCUMENT_PIPELINE_ACTION_SIGN: { variant: 'success', labelKey: 'document.pipeline.action.sign' },
};

export const registrationDirectionKeys: string[] = [
  'DOCUMENT_REGISTRATION_DIRECTION_INCOMING',
  'DOCUMENT_REGISTRATION_DIRECTION_OUTGOING',
  'DOCUMENT_REGISTRATION_DIRECTION_INTERNAL',
];

export function registrationDirectionLabelKey(dir: string | undefined): string {
  switch (dir) {
    case 'DOCUMENT_REGISTRATION_DIRECTION_INCOMING':
      return 'document.register.direction.incoming';
    case 'DOCUMENT_REGISTRATION_DIRECTION_OUTGOING':
      return 'document.register.direction.outgoing';
    case 'DOCUMENT_REGISTRATION_DIRECTION_INTERNAL':
      return 'document.register.direction.internal';
    default:
      return 'document.register.direction.unspecified';
  }
}

export const exportFormats = [
  { value: 'EXPORT_FORMAT_HTML', labelKey: 'document.export.html' },
  { value: 'EXPORT_FORMAT_MARKDOWN', labelKey: 'document.export.markdown' },
];

export function mapping(map: Record<string, Mapping>, key: string | undefined): Mapping {
  if (!key) return { variant: 'secondary', labelKey: 'common.unknown' };
  return map[key] ?? { variant: 'secondary', labelKey: 'common.unknown' };
}

// ---------------------------------------------------------------------------
// Lifecycle transition gating — which actions apply at a given status
// ---------------------------------------------------------------------------

export interface LifecycleAbilities {
  canSubmit: boolean;
  canPublish: boolean;
  canArchive: boolean;
  canRevise: boolean;
}

export function lifecycleAbilities(status: string | undefined): LifecycleAbilities {
  return {
    canSubmit: status === 'DOCUMENT_STATUS_DRAFT' || status === 'DOCUMENT_STATUS_REVISION',
    canPublish: status === 'DOCUMENT_STATUS_APPROVED',
    canArchive:
      status === 'DOCUMENT_STATUS_PUBLISHED' ||
      status === 'DOCUMENT_STATUS_OBSOLETE' ||
      status === 'DOCUMENT_STATUS_APPROVED',
    canRevise: status === 'DOCUMENT_STATUS_PUBLISHED' || status === 'DOCUMENT_STATUS_APPROVED',
  };
}

// ---------------------------------------------------------------------------
// Acknowledgment roll-up
// ---------------------------------------------------------------------------

export function ackRollup(items: any[]): { total: number; done: number; overdue: number; percent: number } {
  const total = items.length;
  let done = 0;
  let overdue = 0;
  for (const a of items) {
    if (a?.status === 'ACKNOWLEDGMENT_STATUS_ACKNOWLEDGED') done++;
    if (a?.status === 'ACKNOWLEDGMENT_STATUS_OVERDUE') overdue++;
  }
  return { total, done, overdue, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
}

// ---------------------------------------------------------------------------
// Small utilities
// ---------------------------------------------------------------------------

function camel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

/** Parse a comma / newline separated list into a trimmed, de-duplicated string array. */
export function parseList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\n,]+/)) {
    const t = part.trim();
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}
